import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, Row, Col, Form, Button, Table, Modal, Alert, Badge } from 'react-bootstrap';
import { FaMoneyBillWave, FaPlus, FaEye, FaPrint, FaEdit, FaTrash } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';

const DoctorPayments = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [doctors, setDoctors] = useState([]);
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [collection, setCollection] = useState(null);
  const [payments, setPayments] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    doctorId: '',
    amountTransferred: '',
    collectionStartDate: new Date().toISOString().split('T')[0],
    collectionEndDate: new Date().toISOString().split('T')[0],
    notes: '',
    receivedBy: ''
  });
  const [error, setError] = useState(null);
  const [periodSummary, setPeriodSummary] = useState(null);
  const [expandedPayments, setExpandedPayments] = useState({});
  const [editingPayment, setEditingPayment] = useState(null);

  useEffect(() => {
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const res = await axios.get('/api/doctors');
      setDoctors(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchCollection = async () => {
    if (!selectedDoctor) return;
    
    try {
      const params = {};
      if (startDate && endDate) {
        params.startDate = startDate;
        params.endDate = endDate;
      }
      
      const res = await axios.get(`/api/doctor-payments/collection/${selectedDoctor}`, { params });
      setCollection(res.data);
      
      const paymentsParams = { doctorId: selectedDoctor, grouped: 'true' };
      if (startDate && endDate) {
        paymentsParams.startDate = startDate;
        paymentsParams.endDate = endDate;
      }
      
      const paymentsRes = await axios.get('/api/doctor-payments', { params: paymentsParams });
      
      setPayments(paymentsRes.data);
    } catch (err) {
      console.error('Error in fetchCollection:', err);
      setError('Failed to load collection data');
    }
  };

  const fetchPeriodSummary = async (doctorId, startDate, endDate) => {
    if (!doctorId || !startDate || !endDate) {
      setPeriodSummary(null);
      return;
    }
    
    try {
      const res = await axios.get(`/api/doctor-payments/collection/${doctorId}`, {
        params: { startDate, endDate }
      });
      setPeriodSummary(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingPayment) {
        await axios.put(`/api/doctor-payments/${editingPayment.id}`, formData);
      } else {
        await axios.post('/api/doctor-payments', formData);
      }
      setShowModal(false);
      setFormData({
        doctorId: '',
        amountTransferred: '',
        collectionStartDate: new Date().toISOString().split('T')[0],
        collectionEndDate: new Date().toISOString().split('T')[0],
        notes: '',
        receivedBy: ''
      });
      setEditingPayment(null);
      setPeriodSummary(null);
      fetchCollection();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.msg || 'Failed to record payment');
    }
  };

  const handleEdit = (payment) => {
    const firstPayment = payment.payments && payment.payments.length > 0 ? payment.payments[0] : payment;
    setFormData({
      doctorId: payment.Doctor?.id || payment.doctorId,
      amountTransferred: firstPayment.amountTransferred,
      collectionStartDate: payment.collectionStartDate.split('T')[0],
      collectionEndDate: payment.collectionEndDate.split('T')[0],
      notes: firstPayment.notes || '',
      receivedBy: firstPayment.receivedBy || ''
    });
    setEditingPayment(firstPayment);
    setShowModal(true);
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm('Are you sure you want to delete this payment record?')) return;
    try {
      await axios.delete(`/api/doctor-payments/${paymentId}`);
      fetchCollection();
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.msg || 'Failed to delete payment');
    }
  };

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString('en-PK', {
      style: 'currency',
      currency: 'PKR'
    });
  };

  const handlePrintReceipt = async () => {
    const doctorName = selectedDoctor ? doctors.find(d => d.id.toString() === selectedDoctor)?.name : 'All Doctors';
    
    const receiptData = { 
      collection, 
      payments, 
      startDate, 
      endDate, 
      doctorName: `Dr. ${doctorName}` 
    };
    
    sessionStorage.setItem('doctorPaymentReceipt', JSON.stringify(receiptData));
    window.open('/doctor-payment-receipt', '_blank');
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Doctor Payments</h2>
        <div>
          {collection && (user?.role === 'admin' || user?.permissions?.printReceipt) && (
            <Button variant="success" className="me-2" onClick={handlePrintReceipt}>
              <FaPrint className="me-2" /> Print Receipt
            </Button>
          )}
          {(user?.role === 'admin' || user?.permissions?.recordPayment) && (
            <Button variant="primary" onClick={() => { setEditingPayment(null); setShowModal(true); }}>
              <FaPlus className="me-2" /> Record Payment
            </Button>
          )}
        </div>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card className="mb-4">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Select Doctor</Form.Label>
                <Form.Select value={selectedDoctor} onChange={(e) => setSelectedDoctor(e.target.value)}>
                  <option value="">-- Select Doctor --</option>
                  {doctors.map(doc => (
                    <option key={doc.id} value={doc.id}>Dr. {doc.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button variant="primary" onClick={fetchCollection} className="w-100">
                <FaEye className="me-2" /> View
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {collection && (
        <Row className="mb-4">
          <Col md={4}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #28a745'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">TOTAL COLLECTED</p>
                <h3 className="mb-0 text-success fw-bold">{formatCurrency(collection.totalCollected)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #007bff'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">TRANSFERRED TO OWNER</p>
                <h3 className="mb-0 text-primary fw-bold">{formatCurrency(collection.totalTransferred)}</h3>
              </Card.Body>
            </Card>
          </Col>
          <Col md={4}>
            <Card className="border-0 shadow-sm" style={{borderLeft: '4px solid #ffc107'}}>
              <Card.Body>
                <p className="text-muted mb-1 small">PENDING WITH DOCTOR</p>
                <h3 className="mb-0 text-warning fw-bold">{formatCurrency(collection.pendingAmount)}</h3>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {payments.length > 0 && (
        <Card>
          <Card.Header>
            <h5 className="mb-0">Payment History</h5>
          </Card.Header>
          <Card.Body>
            <Table responsive hover>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Doctor</th>
                  <th>Period</th>
                  <th>Collected</th>
                  <th>Transferred</th>
                  <th>Transfers</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((payment, idx) => (
                  <React.Fragment key={idx}>
                    <tr>
                      <td>{new Date(payment.transferDate).toLocaleDateString()}</td>
                      <td>Dr. {payment.Doctor?.name}</td>
                      <td>
                        {new Date(payment.collectionStartDate).toLocaleDateString()} - {new Date(payment.collectionEndDate).toLocaleDateString()}
                      </td>
                      <td className="fw-bold">{formatCurrency(payment.totalCollected)}</td>
                      <td className="text-primary fw-bold">{formatCurrency(payment.totalTransferred)}</td>
                      <td><Badge bg="info">{payment.payments?.length || 1} transfer(s)</Badge></td>
                      <td>
                        {(user?.role === 'admin' || user?.permissions?.viewDeposit) && (
                          <Button 
                            variant="outline-secondary" 
                            size="sm"
                            className="me-2"
                            onClick={() => setExpandedPayments({...expandedPayments, [idx]: !expandedPayments[idx]})}
                          >
                            <FaEye className="me-1" /> {expandedPayments[idx] ? 'Hide' : 'View'}
                          </Button>
                        )}
                        {(user?.role === 'admin' || user?.permissions?.printDeposit) && (
                          <Button 
                            variant="outline-primary" 
                            size="sm"
                            className="me-2"
                            onClick={() => {
                              const receiptData = {
                                payment,
                                doctorName: `Dr. ${payment.Doctor?.name}`
                              };
                              sessionStorage.setItem('singlePaymentReceipt', JSON.stringify(receiptData));
                              window.open('/single-payment-receipt', '_blank');
                            }}
                          >
                            <FaPrint className="me-1" /> Print
                          </Button>
                        )}
                        {(user?.role === 'admin' || user?.permissions?.deleteDeposit) && (
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={async () => {
                              if (payment.payments && payment.payments.length > 0) {
                                if (!window.confirm(`Are you sure you want to delete all ${payment.payments.length} transfer(s) for this day?`)) return;
                                try {
                                  await Promise.all(payment.payments.map(p => axios.delete(`/api/doctor-payments/${p.id}`)));
                                  fetchCollection();
                                } catch (err) {
                                  console.error(err);
                                  setError('Failed to delete payment records');
                                }
                              }
                            }}
                            disabled={!payment.payments || payment.payments.length === 0}
                          >
                            <FaTrash className="me-1" /> Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                    
                    {expandedPayments[idx] && (
                      <tr>
                        <td colSpan="7" style={{ backgroundColor: '#f8f9fa', padding: '20px' }}>
                          {payment.payments && payment.payments.length > 0 && (
                            <div className="mb-3">
                              <h6 className="text-muted mb-2">Transfer Details:</h6>
                              <Table responsive size="sm" bordered>
                                <thead className="table-light">
                                  <tr>
                                    <th>Time</th>
                                    <th>Collected</th>
                                    <th>Transferred</th>
                                    <th>Received By</th>
                                    <th>Notes</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {payment.payments.map(p => (
                                    <tr key={p.id}>
                                      <td>{new Date(p.transferDate).toLocaleTimeString()}</td>
                                      <td>{formatCurrency(payment.totalCollected)}</td>
                                      <td className="fw-bold">{formatCurrency(p.amountTransferred)}</td>
                                      <td>{p.receivedBy || '-'}</td>
                                      <td>{p.notes || '-'}</td>
                                      <td>
                                        {(user?.role === 'admin' || user?.permissions?.editTransfer) && (
                                          <Button 
                                            variant="outline-warning" 
                                            size="sm"
                                            className="me-1"
                                            onClick={() => handleEdit({ ...payment, payments: [p] })}
                                          >
                                            <FaEdit />
                                          </Button>
                                        )}
                                        {(user?.role === 'admin' || user?.permissions?.deleteTransfer) && (
                                          <Button 
                                            variant="outline-danger" 
                                            size="sm"
                                            onClick={() => handleDelete(p.id)}
                                          >
                                            <FaTrash />
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </Table>
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}



      <Modal show={showModal} onHide={() => { setShowModal(false); setEditingPayment(null); setPeriodSummary(null); }} size="lg">
        <Modal.Header>
          <Modal.Title>{editingPayment ? 'Edit Payment' : 'Record Payment from Doctor'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Select Doctor *</Form.Label>
              <Form.Select 
                value={formData.doctorId} 
                onChange={(e) => {
                  const doctorId = e.target.value;
                  setFormData({...formData, doctorId});
                  if (doctorId && formData.collectionStartDate && formData.collectionEndDate) {
                    fetchPeriodSummary(doctorId, formData.collectionStartDate, formData.collectionEndDate);
                  }
                }}
                required
              >
                <option value="">-- Select Doctor --</option>
                {doctors.map(doc => (
                  <option key={doc.id} value={doc.id}>Dr. {doc.name}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Select Date *</Form.Label>
              <Form.Control 
                type="date" 
                value={formData.collectionStartDate}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  setFormData({...formData, collectionStartDate: selectedDate, collectionEndDate: selectedDate});
                  if (formData.doctorId && selectedDate) {
                    fetchPeriodSummary(formData.doctorId, selectedDate, selectedDate);
                  }
                }}
                required
              />
            </Form.Group>

            {periodSummary && (
              <Alert variant="info" className="mb-3">
                <strong>Period Summary:</strong>
                <div className="mt-2">
                  <div>Total Collected: <strong className="text-success">{formatCurrency(periodSummary.totalCollected)}</strong></div>
                  <div>Already Transferred: <strong className="text-primary">{formatCurrency(periodSummary.totalTransferred)}</strong></div>
                  <div>Remaining Balance: <strong className="text-warning">{formatCurrency(periodSummary.pendingAmount)}</strong></div>
                </div>
              </Alert>
            )}

            <Form.Group className="mb-3">
              <Form.Label>Amount Transferred *</Form.Label>
              <Form.Control 
                type="number" 
                step="0.01"
                value={formData.amountTransferred}
                onChange={(e) => setFormData({...formData, amountTransferred: e.target.value})}
                placeholder="Enter amount"
                required
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Received By</Form.Label>
              <Form.Control 
                type="text"
                value={formData.receivedBy}
                onChange={(e) => setFormData({...formData, receivedBy: e.target.value})}
                placeholder="Name of person who received"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control 
                as="textarea"
                rows={2}
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Any additional notes"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => { setShowModal(false); setEditingPayment(null); }}>Cancel</Button>
            <Button variant="primary" type="submit">
              <FaMoneyBillWave className="me-2" /> {editingPayment ? 'Update Payment' : 'Record Payment'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default DoctorPayments;
